// Feature flags with localStorage persistence
// Access admin panel: triple-tap the title or add ?admin to URL

const DEFAULT_FLAGS = {
    scrollBlankTileIntoView: false,
    autoAwayMinutes: 5,
    autoEndWhenAllIdle: false,
    tileBag: '72',
};

// Settings that use dropdowns instead of checkboxes
const DROPDOWN_SETTINGS = {
    tileBag: {
        label: 'Tile Bag',
        options: {
            // '72' is the legacy default value; it now means "random named bag"
            '72': 'Random (surprise me)',
            'houseBlend': 'House Blend',
            'vowelPlay': 'Vowel Play',
            'consonantCrunch': 'Consonant Crunch',
            'spiceRack': 'Spice Rack',
            'decaf': 'Decaf',
            'steve': 'Steve',
            'carl': 'Carl',
            '60': 'Compact 60',
            '65': 'Light 65',
            '80': 'Extended 80'
        }
    }
};

// Settings that use number inputs
const NUMBER_SETTINGS = {
    autoAwayMinutes: {
        label: 'Auto-Away Timer (minutes)',
        min: 1,
        max: 30,
        step: 1
    }
};

class FeatureFlags {
    constructor() {
        this.flags = { ...DEFAULT_FLAGS };
        this.onFlagChanged = null; // Callback for multiplayer sync
        this.load();
    }

    load() {
        try {
            const stored = localStorage.getItem('wordsheet_flags');
            if (stored) {
                const parsed = JSON.parse(stored);
                this.flags = { ...DEFAULT_FLAGS, ...parsed };
            }
        } catch (e) {
            console.warn('Failed to load feature flags:', e);
        }
    }

    save() {
        try {
            localStorage.setItem('wordsheet_flags', JSON.stringify(this.flags));
        } catch (e) {
            console.warn('Failed to save feature flags:', e);
        }
    }

    get(flag) {
        return this.flags[flag] ?? DEFAULT_FLAGS[flag] ?? false;
    }

    set(flag, value) {
        this.flags[flag] = value;
        this.save();
        this.onFlagChanged?.(flag, value);
    }

    toggle(flag) {
        this.set(flag, !this.get(flag));
        return this.get(flag);
    }

    all() {
        return { ...this.flags };
    }

    // Apply game-level flag overrides from Firestore (synced across players)
    applyGameFlags(gameFlags) {
        if (!gameFlags) return;
        // Only update if something actually changed
        let changed = false;
        for (const [key, value] of Object.entries(gameFlags)) {
            if (this.flags[key] !== value) {
                this.flags[key] = value;
                changed = true;
            }
        }
        if (changed) {
            this.save();
            refreshAdminPanel();
        }
    }

    reset() {
        this.flags = { ...DEFAULT_FLAGS };
        this.save();
        this.onFlagChanged?.('__reset', this.flags);
    }
}

export const flags = new FeatureFlags();

// Admin panel UI
let adminPanel = null;

function refreshAdminPanel() {
    if (adminPanel) {
        adminPanel.remove();
        adminPanel = null;
        showAdminPanel();
    }
}

function getAdminStatsUrl() {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocal) {
        return 'http://localhost:8787/admin/stats';
    }
    return 'https://wordsheet-api.adam-henson.workers.dev/admin/stats';
}

export function showAdminPanel() {
    if (adminPanel) {
        adminPanel.remove();
        adminPanel = null;
        return;
    }

    adminPanel = document.createElement('div');
    adminPanel.className = 'admin-panel';
    adminPanel.innerHTML = `
        <div class="admin-panel-header">
            <h3>Feature Flags</h3>
            <button class="admin-close">&times;</button>
        </div>
        <div class="admin-panel-content"></div>
        <div class="admin-panel-footer">
            <div class="admin-footer-actions">
                <a class="admin-open-stats" href="${getAdminStatsUrl()}" target="_blank" rel="noopener">Open Admin Stats</a>
            </div>
            <div class="admin-footer-right">
                <button class="admin-reset">Reset All</button>
            </div>
        </div>
    `;

    const content = adminPanel.querySelector('.admin-panel-content');
    const allFlags = flags.all();

    for (const [key, value] of Object.entries(allFlags)) {
        const row = document.createElement('div');
        row.className = 'admin-flag-row';

        const dropdownConfig = DROPDOWN_SETTINGS[key];
        const numberConfig = NUMBER_SETTINGS[key];

        if (dropdownConfig) {
            // Render as dropdown
            const label = document.createElement('span');
            label.className = 'admin-flag-name';
            label.textContent = dropdownConfig.label;
            row.appendChild(label);

            const select = document.createElement('select');
            select.dataset.flag = key;
            select.className = 'admin-select';
            for (const [optKey, optLabel] of Object.entries(dropdownConfig.options)) {
                const option = document.createElement('option');
                option.value = optKey;
                option.textContent = optLabel;
                if (optKey === value) option.selected = true;
                select.appendChild(option);
            }
            row.appendChild(select);
        } else if (numberConfig) {
            // Render as number input
            const label = document.createElement('span');
            label.className = 'admin-flag-name';
            label.textContent = numberConfig.label;
            row.appendChild(label);

            const input = document.createElement('input');
            input.type = 'number';
            input.dataset.flag = key;
            input.className = 'admin-number';
            input.value = value;
            input.min = numberConfig.min;
            input.max = numberConfig.max;
            input.step = numberConfig.step;
            row.appendChild(input);
        } else {
            // Render as checkbox
            const label = document.createElement('label');
            label.className = 'admin-checkbox-label';
            label.innerHTML = `
                <span class="admin-flag-name">${formatFlagName(key)}</span>
                <input type="checkbox" ${value ? 'checked' : ''} data-flag="${key}">
            `;
            row.appendChild(label);
        }

        content.appendChild(row);
    }

    // Event handlers
    adminPanel.querySelector('.admin-close').addEventListener('click', () => {
        adminPanel.remove();
        adminPanel = null;
    });

    adminPanel.querySelector('.admin-reset').addEventListener('click', () => {
        flags.reset();
        // Re-render (reset already removed+re-created via refreshAdminPanel if callback triggers it,
        // but do it explicitly in case there's no multiplayer callback)
        if (adminPanel) {
            adminPanel.remove();
            adminPanel = null;
        }
        showAdminPanel();
    });

    content.addEventListener('change', (e) => {
        if (e.target.dataset.flag) {
            let value;
            if (e.target.type === 'checkbox') {
                value = e.target.checked;
            } else if (e.target.type === 'number') {
                value = parseFloat(e.target.value);
            } else {
                value = e.target.value;
            }
            flags.set(e.target.dataset.flag, value);
        }
    });

    document.body.appendChild(adminPanel);
}

function formatFlagName(key) {
    // Convert camelCase to Title Case with spaces
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
}

// Triple-tap detection for title
let tapCount = 0;
let tapTimeout = null;

export function initAdminTrigger() {
    // URL param trigger
    if (window.location.search.includes('admin')) {
        showAdminPanel();
    }

    // Triple-tap on any title (h1)
    document.addEventListener('click', (e) => {
        if (e.target.tagName === 'H1') {
            tapCount++;
            clearTimeout(tapTimeout);

            if (tapCount >= 3) {
                tapCount = 0;
                showAdminPanel();
            } else {
                tapTimeout = setTimeout(() => {
                    tapCount = 0;
                }, 500);
            }
        }
    });
}
